import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDate, TZ_OPTIONS, getTz, setTz } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageTitle, Field, StatusBadge } from "../components/kit";
import { Switch } from "../components/ui/switch";
import { AlertsCard } from "../components/PushAlerts";
import { SignedInDevices } from "../components/SignedInDevices";

export default function Account() {
  const { user, refresh, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: user.name, phone: user.phone || "", addresses: (user.addresses || []).join("\n"), notification_prefs: user.notification_prefs || {}, timezone: user.timezone || getTz() });
  const [pw, setPw] = useState({ current: "", new: "" });
  const [tickets, setTickets] = useState([]);
  useEffect(() => { api.get("/tickets/mine").then((r) => setTickets(r.data)); }, []);
  const save = async () => {
    try { await api.put("/account", { ...f, addresses: f.addresses.split("\n").filter(Boolean) }); setTz(f.timezone); await refresh(); toast.success("Account updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  const changePw = async () => { try { await api.post("/account/password", pw); toast.success("Password changed"); setPw({ current: "", new: "" }); } catch (e) { toast.error(errMsg(e)); } };
  const exportData = async () => {
    const { data } = await api.get("/account/export");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    a.download = "myhomelyhub-data.json";
    a.click();
  };
  const del = async () => {
    if (!window.confirm("Permanently delete your account? This cannot be undone.")) return;
    await api.delete("/account");
    setUser(false);
    nav("/");
  };
  const pref = (k) => <div key={k} className="flex items-center justify-between py-2"><span className="text-sm capitalize">{k === "in_app" ? "In-app" : k === "push" ? "App & phone" : k} notifications</span><Switch checked={k === "push" ? f.notification_prefs.push !== false : !!f.notification_prefs[k]} onCheckedChange={(v) => setF({ ...f, notification_prefs: { ...f.notification_prefs, [k]: v } })} data-testid={`pref-${k}`} /></div>;
  return (
    <div className="max-w-3xl space-y-6">
      <PageTitle title="Account" subtitle={`${user.email}${user.email_verified ? " · verified" : " · not verified yet"}`} />
      <section className="card p-6 space-y-4">
        <h2 className="font-bold">Profile</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name"><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="account-name" /></Field>
          <Field label="Phone"><input className="input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="account-phone" /></Field>
          <Field label="Time zone" hint="Dates show as dd/mm/yyyy. Times show in this zone (GMT by default)."><select className="input" value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} data-testid="account-timezone">{TZ_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
        </div>
        <Field label="Addresses (one per line)"><textarea className="input" value={f.addresses} onChange={(e) => setF({ ...f, addresses: e.target.value })} data-testid="account-addresses" /></Field>
        <div className="divide-y divide-slate-100">{["email", "in_app", "push", "sms", "marketing"].map(pref)}</div>
        <button onClick={save} className="btn-navy" data-testid="account-save-btn">Save changes</button>
      </section>
      <AlertsCard />
      <section className="card p-6 space-y-4">
        <h2 className="font-bold">Password & security</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Current password"><input type="password" className="input" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="account-current-pw" /></Field>
          <Field label="New password"><input type="password" className="input" value={pw.new} onChange={(e) => setPw({ ...pw, new: e.target.value })} data-testid="account-new-pw" /></Field>
        </div>
        <button onClick={changePw} className="btn-ghost" data-testid="account-change-pw-btn">Change password</button>
      </section>
      <SignedInDevices />
      <section className="card p-6">
        <h2 className="font-bold mb-2">Support tickets</h2>
        {tickets.length === 0 ? <p className="text-sm muted">No tickets. Need help? Use the Contact page.</p> : tickets.map((t) => <div key={t.id} className="flex justify-between py-2 border-b border-slate-50 text-sm"><span>{t.ref} · {t.subject || t.category}</span><span className="flex gap-2 items-center">{fmtDate(t.at)}<StatusBadge status={t.status} /></span></div>)}
      </section>
      <section className="card p-6">
        <h2 className="font-bold">Privacy (UK GDPR)</h2>
        <p className="text-sm muted mt-1">Download a copy of your data or permanently delete your account.</p>
        <div className="flex gap-2 mt-4"><button onClick={exportData} className="btn-ghost" data-testid="account-export-btn">Export my data</button>
          {user.role !== "admin" && <button onClick={del} className="btn bg-rose-50 text-rose-700 hover:bg-rose-100" data-testid="account-delete-btn">Delete account</button>}</div>
      </section>
    </div>
  );
}
